import * as THREE from 'three';
import { loadLevel, type LevelData } from '@/world/LevelLoader';
import type { CollisionWorld } from '@/world/collision';
import type { LightPool } from '@/fx/lighting';
import { levelDataFromBlueprint, type DistrictBlueprint } from '@/world/DistrictBlueprint';
import { CANAL_DEPTH, QUAY_Z, cityGround } from '@/world/districts/harbor';

const STREET_Z = 0;
const DISTRICT_X = 216;
const DISTRICT_WIDTH = 96;
/** Smaller kadedek dan in de haven: hier staat een woonrij aan het water, geen loodsen. */
const QUAY_DECK_DEPTH = 3.4;

export const WORKERS = {
  entry: new THREE.Vector2(172, STREET_Z),
  safehouse: new THREE.Vector2(240, -5.4),
  safehouseRadius: 2.25,
  streetZ: STREET_Z,
  x: DISTRICT_X,
  width: DISTRICT_WIDTH,
} as const;

const WORKERS_BUILDINGS: LevelData['buildings'] = [
  { kind: 'tenement', x: 174, z: -13.2, width: 10, depth: 10.4, floors: 5, facing: 'south', wall: 'oxideMaroon', seed: 'workers-tenement-entry', batch: 'workers-north' },
  { kind: 'tenement', x: 185, z: -13.2, width: 10, depth: 10.4, floors: 4, facing: 'south', wall: 'nightBrick', seed: 'workers-tenement-1', batch: 'workers-north' },
  { kind: 'canalHouse', x: 198, z: -13, width: 4, depth: 8, floors: 4, facing: 'south', wall: 'nightBrick', seed: 'workers-canal-1', batch: 'workers-north' },
  { kind: 'canalHouse', x: 204, z: -13, width: 4, depth: 8, floors: 4, facing: 'south', wall: 'slateGreen', seed: 'workers-canal-2', batch: 'workers-north' },
  { kind: 'tenement', x: 217, z: -13.2, width: 10, depth: 10.4, floors: 5, facing: 'south', wall: 'nightBrick', seed: 'workers-tenement-2', batch: 'workers-north' },
  { kind: 'tenement', x: 229, z: -13.2, width: 10, depth: 10.4, floors: 4, facing: 'south', wall: 'oxideMaroon', seed: 'workers-tenement-3', batch: 'workers-north' },
  { kind: 'canalHouse', x: 240, z: -12.8, width: 4, depth: 8, floors: 4, facing: 'south', wall: 'nightBrick', seed: 'workers-safehouse', batch: 'workers-north' },
  { kind: 'canalHouse', x: 248, z: -13, width: 4, depth: 8, floors: 4, facing: 'south', wall: 'slateGreen', seed: 'workers-canal-3', batch: 'workers-north' },
  { kind: 'tenement', x: 258, z: -13.2, width: 10, depth: 10.4, floors: 5, facing: 'south', wall: 'brickRed', seed: 'workers-tenement-6', batch: 'workers-north' },
  { kind: 'canalHouse', x: 176, z: 13, width: 4, depth: 8, floors: 4, facing: 'north', wall: 'nightBrick', seed: 'workers-pub-entry', batch: 'workers-south' },
  { kind: 'canalHouse', x: 188, z: 13, width: 4, depth: 8, floors: 4, facing: 'north', wall: 'slateGreen', seed: 'workers-pub-1', batch: 'workers-south' },
  { kind: 'canalHouse', x: 194, z: 13, width: 4, depth: 8, floors: 4, facing: 'north', wall: 'oxideMaroon', seed: 'workers-pub-2', batch: 'workers-south' },
  { kind: 'canalHouse', x: 200, z: 13, width: 4, depth: 8, floors: 4, facing: 'north', wall: 'nightBrick', seed: 'workers-pub-3', batch: 'workers-south' },
  { kind: 'tenement', x: 214, z: 13.2, width: 10, depth: 10.4, floors: 4, facing: 'north', wall: 'slateGreen', seed: 'workers-tenement-4', batch: 'workers-south' },
  { kind: 'tenement', x: 225, z: 13.2, width: 10, depth: 10.4, floors: 5, facing: 'north', wall: 'oxideMaroon', seed: 'workers-tenement-4b', batch: 'workers-south' },
  // Drie verdiepingen schip (9 m) met een toren van ~17 m: hoog genoeg om als landmark boven
  // de tenements van vijf hoog uit te komen. Stond eerder op 6, wat als 18 m schip zou lezen.
  // De diepte is teruggebracht zodat het schip achter de kadelijn blijft.
  { kind: 'church', x: 236, z: 13.2, width: 8, depth: 10.4, floors: 3, facing: 'north', wall: 'nightStone', seed: 'workers-church', batch: 'workers-south' },
  { kind: 'tenement', x: 250, z: 13.2, width: 10, depth: 10.4, floors: 4, facing: 'north', wall: 'nightBrick', seed: 'workers-tenement-5', batch: 'workers-south' },
  { kind: 'tenement', x: 261, z: 13.2, width: 8, depth: 10.4, floors: 5, facing: 'north', wall: 'oxideMaroon', seed: 'workers-tenement-7', batch: 'workers-south' },
  // Achterwand: de wijk liep tot z = -36 door zonder dat daar iets stond, wat als een leeg
  // asfaltveld las. Dezelfde oplossing als in de haven en de connector.
  { kind: 'warehouse', x: 174, z: -30, width: 12, depth: 6, floors: 3, facing: 'south', wall: 'nightBrick', seed: 'workers-back-0', batch: 'workers-back' },
  { kind: 'warehouse', x: 190, z: -30, width: 12, depth: 6, floors: 2, facing: 'south', wall: 'brickRed', seed: 'workers-back-1', batch: 'workers-back' },
  { kind: 'warehouse', x: 206, z: -30, width: 12, depth: 6, floors: 4, facing: 'south', wall: 'slateGreen', seed: 'workers-back-2', batch: 'workers-back' },
  { kind: 'warehouse', x: 222, z: -30, width: 12, depth: 6, floors: 2, facing: 'south', wall: 'oxideMaroon', seed: 'workers-back-3', batch: 'workers-back' },
  { kind: 'warehouse', x: 238, z: -30, width: 12, depth: 6, floors: 3, facing: 'south', wall: 'nightBrick', seed: 'workers-back-4', batch: 'workers-back' },
  { kind: 'warehouse', x: 254, z: -30, width: 12, depth: 6, floors: 2, facing: 'south', wall: 'brickRed', seed: 'workers-back-5', batch: 'workers-back' },
];

export const WORKERS_BLUEPRINT: DistrictBlueprint = {
  id: 'workers',
  levelName: 'NA_District_Workers',
  districtName: 'De Arbeidersbuurt',
  primaryRoute: 'Straight neighborhood avenue between dense tenements and pub fronts.',
  landmarks: ['Church block and Vitale safehouse row.'],
  ground: cityGround(DISTRICT_WIDTH, DISTRICT_X),
  quayWall: {
    length: DISTRICT_WIDTH,
    x: DISTRICT_X,
    z: QUAY_Z,
  },
  quayDeck: {
    length: DISTRICT_WIDTH,
    depth: QUAY_DECK_DEPTH,
    x: DISTRICT_X,
    z: QUAY_Z - QUAY_DECK_DEPTH / 2,
  },
  water: {
    width: DISTRICT_WIDTH,
    depth: CANAL_DEPTH,
    x: DISTRICT_X,
    z: QUAY_Z + CANAL_DEPTH / 2,
  },
  // Het kanaal liep eerder abrupt dood op x = 168. Nu loopt dezelfde waterlijn door langs de
  // hele zuidrand van de stad, met dezelfde kademuur ervoor.
  obstacles: [{ x: DISTRICT_X, z: QUAY_Z + 1, halfX: DISTRICT_WIDTH / 2, halfZ: 1, height: 2 }],
  bridges: [],
  roads: [
    {
      id: 'workers-road-eastbound',
      x: 218,
      z: 1.2,
      halfX: 46,
      halfZ: 1.35,
      intent: 'Eastbound neighborhood lane kept clear.',
      route: {
        id: 'workers-main-eastbound',
        kind: 'sedan',
        points: [
          new THREE.Vector2(172, 1.2),
          new THREE.Vector2(200, 1.2),
          new THREE.Vector2(232, 1.2),
          new THREE.Vector2(264, 1.2),
        ],
        speedLimit: 3.1,
        loop: true,
      },
    },
    {
      id: 'workers-road-westbound',
      x: 218,
      z: -2.8,
      halfX: 46,
      halfZ: 1.35,
      intent: 'Westbound neighborhood lane kept clear.',
      route: {
        id: 'workers-main-westbound',
        kind: 'van',
        points: [
          new THREE.Vector2(264, -2.8),
          new THREE.Vector2(234, -2.8),
          new THREE.Vector2(202, -2.8),
          new THREE.Vector2(172, -2.8),
        ],
        speedLimit: 2.6,
        loop: true,
      },
    },
  ],
  pedestrianZones: [
    { id: 'workers-north-sidewalk', kind: 'sidewalk', x: 218, z: -5.4, halfX: 48, halfZ: 1.2, intent: 'North sidewalk with streetlamps and safehouse access.', allowedProps: ['hydrant', 'phoneBooth', 'trafficLight'] },
    { id: 'workers-south-sidewalk', kind: 'sidewalk', x: 218, z: 5.2, halfX: 48, halfZ: 1.1, intent: 'South sidewalk with lamp line, bollards and small cover pockets.', allowedProps: ['bollard', 'crateStack'] },
    { id: 'workers-south-cover-pockets', kind: 'serviceYard', x: 213, z: 4.35, halfX: 42, halfZ: 0.65, intent: 'Small loading and cover pockets along the pub-side curb.', allowedProps: ['crateStack'] },
    { id: 'workers-pub-fronts', kind: 'buildingFootprint', x: 213, z: 8.5, halfX: 43, halfZ: 1.1, intent: 'Facade strip for pub and storefront signs.', allowedProps: ['neonSign'] },
    { id: 'workers-church-plaza', kind: 'objectiveArea', x: 236, z: 7.0, halfX: 8, halfZ: 1.8, intent: 'Readable plaza in front of the church landmark.' },
    { id: 'workers-parking-north', kind: 'parkingBay', x: 215, z: -4.1, halfX: 46, halfZ: 0.9, intent: 'Curbside parking along the north sidewalk.', allowedVehicles: ['sedan', 'van', 'wreck'] },
    { id: 'workers-parking-south', kind: 'parkingBay', x: 215, z: 4.2, halfX: 46, halfZ: 0.9, intent: 'Curbside parking along pub fronts.', allowedVehicles: ['sedan', 'van', 'wreck'] },
    { id: 'workers-safehouse-zone', kind: 'objectiveArea', x: WORKERS.safehouse.x, z: WORKERS.safehouse.y, halfX: 3.2, halfZ: 1.4, intent: 'Safehouse entry and mission-complete area.' },
  ],
  buildingBlocks: [
    {
      id: 'workers-north-tenement-row',
      intent: 'Dense residential north row with the Vitale safehouse as a readable address.',
      buildings: WORKERS_BUILDINGS.filter((placement) => placement.batch === 'workers-north'),
    },
    {
      id: 'workers-south-pub-and-church-row',
      intent: 'South row mixes pubs, small homes and the church landmark with clear frontage.',
      buildings: WORKERS_BUILDINGS.filter((placement) => placement.batch === 'workers-south'),
    },
    {
      id: 'workers-back-industrial-wall',
      intent: 'Back-row workshops close the north side of the neighbourhood, matching harbor and connector.',
      buildings: WORKERS_BUILDINGS.filter((placement) => placement.batch === 'workers-back'),
    },
  ],
  streetlamps: [
    { x: 174, z: -5.5, rotation: 0 },
    { x: 184, z: -5.5, rotation: 0 },
    { x: 204, z: -5.5, rotation: 0 },
    { x: 226, z: -5.5, rotation: 0 },
    { x: 248, z: -5.5, rotation: 0 },
    { x: 264, z: -5.5, rotation: 0 },
    { x: 176, z: 5.5, rotation: Math.PI },
    { x: 194, z: 5.5, rotation: Math.PI },
    { x: 218, z: 5.5, rotation: Math.PI },
    { x: 242, z: 5.5, rotation: Math.PI },
    { x: 262, z: 5.5, rotation: Math.PI },
  ],
  propClusters: [
    {
      id: 'workers-pub-signs',
      zoneId: 'workers-pub-fronts',
      intent: 'Neon signage marks the nightlife frontage without blocking sidewalks.',
      props: [
        { kind: 'neonSign', x: 176, z: 8.4, rotation: 0 },
        { kind: 'neonSign', x: 188, z: 8.4, rotation: 0 },
        { kind: 'neonSign', x: 194, z: 8.4, rotation: 0 },
        { kind: 'neonSign', x: 200, z: 8.4, rotation: 0 },
        { kind: 'neonSign', x: 214, z: 8.4, rotation: 0 },
        { kind: 'neonSign', x: 226, z: 8.4, rotation: 0 },
        { kind: 'neonSign', x: 250, z: 8.4, rotation: 0 },
        { kind: 'neonSign', x: 261, z: 8.4, rotation: 0 },
      ],
    },
    {
      id: 'workers-curb-cover',
      zoneId: 'workers-south-cover-pockets',
      intent: 'Crate stacks create readable cover pockets along the pub side, outside lanes.',
      props: [
        { kind: 'crateStack', x: 174, z: 4.6, rotation: 0.3 },
        { kind: 'crateStack', x: 194, z: 4.2, rotation: -0.18 },
        { kind: 'crateStack', x: 218, z: 4.2, rotation: -0.25 },
        { kind: 'crateStack', x: 248, z: 4.4, rotation: 0.2 },
      ],
    },
    {
      id: 'workers-plaza-edge',
      zoneId: 'workers-south-sidewalk',
      intent: 'A single bollard marks the church/plaza edge without cluttering the route.',
      props: [
        { kind: 'bollard', x: 226, z: 5.4, rotation: 0 },
        { kind: 'bollard', x: 238, z: 5.4, rotation: 0 },
        { kind: 'bollard', x: 250, z: 5.4, rotation: 0 },
      ],
    },
  ],
  parkedVehicles: [
    {
      id: 'workers-parked-van-1',
      kind: 'van',
      position: new THREE.Vector2(182, 4.2),
      rotation: Math.PI / 2,
      stealable: true,
    },
    {
      id: 'workers-parked-sedan-1',
      kind: 'sedan',
      position: new THREE.Vector2(248, -4.1),
      rotation: -Math.PI / 2,
      stealable: false,
    },
    {
      id: 'workers-parked-wreck-1',
      kind: 'wreck',
      position: new THREE.Vector2(214, 4.2),
      rotation: Math.PI / 2,
      stealable: false,
    },
  ],
  safehouse: {
    x: WORKERS.safehouse.x,
    z: WORKERS.safehouse.y,
    radius: WORKERS.safehouseRadius,
  },
};

export const WORKERS_LEVEL: LevelData = levelDataFromBlueprint(WORKERS_BLUEPRINT);

export function buildWorkersDistrict(scene: THREE.Scene, collision: CollisionWorld, lights: LightPool): void {
  loadLevel(scene, collision, lights, WORKERS_LEVEL);
}
